<script setup>
import { useSeo } from '@/composables/useSeo'

useSeo('agencies')
import { onMounted } from 'vue'
import genImages from '@/generated/gen-images.json'
import HalftoneVeil from '@/components/HalftoneVeil.vue'
import {
  BuildingOffice2Icon,
  UserGroupIcon,
  EyeIcon,
  CubeIcon,
  DevicePhoneMobileIcon,
  DocumentChartBarIcon,
  CurrencyDollarIcon,
  ServerStackIcon,
  CheckIcon,
  InboxArrowDownIcon,
  SwatchIcon,
  ClipboardDocumentListIcon,
  BanknotesIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/vue/24/outline'

// Operations grid — copy lives in i18n (agency.ops.items), icons here
const opsIcons = [
  BuildingOffice2Icon,
  UserGroupIcon,
  EyeIcon,
  CubeIcon,
  DevicePhoneMobileIcon,
  DocumentChartBarIcon
]

// Partner program — copy in i18n (agency.program.items), icons here
const programIcons = [CurrencyDollarIcon, ServerStackIcon]

// Bifrost Pro (écosystème) — copy in i18n (agency.pro.items), icons here
const proIcons = [InboxArrowDownIcon, SwatchIcon, ClipboardDocumentListIcon, BanknotesIcon]

onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-reveal')
        observer.unobserve(entry.target)
      }
    })
  }, { threshold: 0.1 })

  document.querySelectorAll('.reveal-on-scroll').forEach((el) => {
    observer.observe(el)
  })
})
</script>

<template>
  <!-- ================= HERO ================= -->
  <header class="relative overflow-hidden bg-slate-950 pt-40 pb-28">
    <!-- Visuel de fond + voile de lisibilité -->
    <div class="absolute inset-0">
      <img :src="genImages['agencies-hero']" alt="" aria-hidden="true"
        class="w-full h-full object-cover opacity-70 brightness-110" />
      <div class="absolute inset-0 bg-slate-950/45"></div>
      <div class="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-slate-950 to-transparent"></div>
      <div class="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-slate-950 to-transparent"></div>
    </div>
    <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out">
        <div
          class="inline-flex items-center px-3 py-1.5 border border-white/15 text-slate-300 text-xs font-mono tracking-wide mb-10 rounded-md">
          <span class="flex h-1.5 w-1.5 rounded-full bg-primary-400 me-2"></span>
          {{ $t('agency.badge') }}
        </div>

        <h1 class="text-5xl md:text-7xl font-semibold text-white leading-[1.05]">
          {{ $t('agency.hero.title') }}<br />
          <span class="text-primary-400">{{ $t('agency.hero.accent') }}</span>
        </h1>

        <p class="mt-8 max-w-2xl mx-auto text-lg md:text-xl text-slate-400 leading-relaxed">
          {{ $t('agency.hero.desc') }}
        </p>

        <div class="mt-10 flex flex-col sm:flex-row justify-center gap-3">
          <a href="https://bifrost.events/touch" target="_blank"
            class="px-7 py-3.5 bg-white text-slate-950 rounded-md font-semibold text-base hover:bg-slate-200 transition">
            {{ $t('agency.hero.primary') }}
          </a>
          <a href="#program"
            class="px-7 py-3.5 border border-white/20 text-white rounded-md font-semibold text-base hover:bg-white/10 transition">
            {{ $t('agency.hero.secondary') }}
          </a>
        </div>
      </div>
    </div>
  </header>

  <!-- ================= OPERATIONS ================= -->
  <section class="py-28 bg-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="max-w-3xl mb-16 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <p class="text-xs font-mono text-primary-600 tracking-widest uppercase mb-4">{{ $t('agency.ops.label') }}</p>
        <h2 class="text-4xl md:text-5xl font-semibold text-slate-900 mb-5">{{ $t('agency.ops.title') }}</h2>
        <p class="text-lg text-slate-500">{{ $t('agency.ops.desc') }}</p>
      </div>

      <div
        class="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <div v-for="(icon, i) in opsIcons" :key="i"
          class="group bg-white p-8 hover:bg-slate-50 transition-colors duration-200">
          <div class="flex items-start justify-between mb-6">
            <component :is="icon" class="w-7 h-7 text-slate-900" />
            <span class="text-[11px] font-mono text-slate-400 border border-slate-200 rounded px-2 py-0.5">
              {{ $t(`agency.ops.items[${i}].tag`) }}
            </span>
          </div>
          <h3 class="text-lg font-semibold text-slate-900 mb-2">{{ $t(`agency.ops.items[${i}].title`) }}</h3>
          <p class="text-sm text-slate-500 leading-relaxed">{{ $t(`agency.ops.items[${i}].desc`) }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= WHITE LABEL ================= -->
  <section class="py-28 bg-slate-950 text-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid lg:grid-cols-2 gap-16 items-center">

        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <p class="text-xs font-mono text-primary-400 tracking-widest uppercase mb-4">{{ $t('agency.brand.label') }}</p>
          <h2 class="text-4xl md:text-5xl font-semibold mb-6">{{ $t('agency.brand.title') }}</h2>
          <p class="text-lg text-slate-400 leading-relaxed">{{ $t('agency.brand.desc') }}</p>
        </div>

        <div
          class="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <div v-for="i in 4" :key="i" class="bg-slate-950 p-8">
            <CheckIcon class="w-5 h-5 text-primary-400 mb-4" />
            <h3 class="text-base font-semibold text-white mb-2">{{ $t(`agency.brand.items[${i - 1}].title`) }}</h3>
            <p class="text-sm text-slate-400 leading-relaxed">{{ $t(`agency.brand.items[${i - 1}].desc`) }}</p>
          </div>
        </div>

      </div>
    </div>
  </section>

  <!-- ================= SHOWCASE — marque blanche (supports vierges) ================= -->
  <section class="bg-slate-950">
    <div class="relative overflow-hidden aspect-[3/1] reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
      <img :src="genImages['agencies-whitelabel']"
        alt="Scène et supports d'affichage vierges d'un événement en cours d'installation, prêts à être habillés aux couleurs d'une marque"
        class="w-full h-full object-cover" loading="lazy" />
      <!-- Raccord haut (section sombre) : fondu discret côté nuit -->
      <div class="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-slate-950/20 to-transparent"></div>
      <!-- Raccord bas (section slate-50) : trame demi-teinte, pas de voile laiteux -->
      <HalftoneVeil color="#f8fafc" side="bottom" class="absolute inset-0" />
    </div>
  </section>

  <!-- ================= PARTNER PROGRAM ================= -->
  <section id="program" class="py-28 bg-slate-50 border-t border-slate-200">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="max-w-3xl mb-16 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <p class="text-xs font-mono text-primary-600 tracking-widest uppercase mb-4">{{ $t('agency.program.label') }}</p>
        <h2 class="text-4xl md:text-5xl font-semibold text-slate-900 mb-5">{{ $t('agency.program.title') }}</h2>
        <p class="text-lg text-slate-500">{{ $t('agency.program.desc') }}</p>
      </div>

      <div
        class="grid md:grid-cols-2 gap-px bg-slate-200 border border-slate-200 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <div v-for="(icon, i) in programIcons" :key="i" class="bg-white p-8">
          <component :is="icon" class="w-7 h-7 text-slate-900 mb-6" />
          <h3 class="text-lg font-semibold text-slate-900 mb-2">{{ $t(`agency.program.items[${i}].title`) }}</h3>
          <p class="text-sm text-slate-500 leading-relaxed">{{ $t(`agency.program.items[${i}].desc`) }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= BIFROST PRO — écosystème ================= -->
  <section class="py-28 bg-slate-950 text-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid lg:grid-cols-2 gap-16 items-center">

        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <p class="text-xs font-mono text-primary-400 tracking-widest uppercase mb-4">{{ $t('agency.pro.label') }}</p>
          <h2 class="text-4xl md:text-5xl font-semibold mb-6">{{ $t('agency.pro.title') }}</h2>
          <p class="text-lg text-slate-400 leading-relaxed">{{ $t('agency.pro.desc') }}</p>

          <div class="mt-8 flex flex-col sm:flex-row gap-3">
            <a href="https://www.bifrost-events.pro" target="_blank" rel="noopener"
              class="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-slate-950 rounded-md font-semibold text-base hover:bg-slate-200 transition">
              {{ $t('agency.pro.cta') }}
              <ArrowTopRightOnSquareIcon class="w-4 h-4" />
            </a>
            <a href="https://docs.bifrost-events.pro" target="_blank" rel="noopener"
              class="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-white/20 text-white rounded-md font-semibold text-base hover:bg-white/10 transition">
              {{ $t('agency.pro.docs') }}
              <ArrowTopRightOnSquareIcon class="w-4 h-4" />
            </a>
          </div>
          <p class="mt-5 text-sm text-slate-500">{{ $t('agency.pro.note') }}</p>
        </div>

        <div
          class="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <div v-for="(icon, i) in proIcons" :key="i" class="bg-slate-950 p-8">
            <component :is="icon" class="w-5 h-5 text-primary-400 mb-4" />
            <h3 class="text-base font-semibold text-white mb-2">{{ $t(`agency.pro.items[${i}].title`) }}</h3>
            <p class="text-sm text-slate-400 leading-relaxed">{{ $t(`agency.pro.items[${i}].desc`) }}</p>
          </div>
        </div>

      </div>
    </div>
  </section>

  <!-- ================= CTA ================= -->
  <section class="py-24 bg-white">
    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div
        class="bg-slate-950 rounded-lg p-12 md:p-20 text-center reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <h2 class="text-3xl md:text-5xl font-semibold text-white mb-6">{{ $t('agency.cta.title') }}</h2>
        <p class="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">{{ $t('agency.cta.desc') }}</p>
        <a href="https://bifrost.events/touch" target="_blank"
          class="inline-block px-7 py-3.5 bg-white text-slate-950 font-semibold rounded-md text-base hover:bg-slate-200 transition">
          {{ $t('agency.cta.btn') }}
        </a>
      </div>
    </div>
  </section>
</template>

<style>
</style>
